// v1.9 P0: Button 原子组件
// 4 variants × 3 sizes + loading + icon slots
// wabi-sabi 配色: 茶色 #8b6f47 主色, 暖白 #faf7f2 surface
//
// 设计原则:
// - 默认 size=md, variant=primary
// - loading 时禁用 + 替换内容为 spinner + 当前文字
// - 全宽 block 用于 mobile 表单底部
// - 所有状态有 focus-visible ring (a11y)
import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon } from '../Icon';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** 显示 loading 状态: 禁用 + 显示 spinner */
  loading?: boolean;
  /** 左侧 icon (Icon 组件 name) */
  iconLeft?: string;
  /** 右侧 icon */
  iconRight?: string;
  /** 全宽 */
  block?: boolean;
  /** 按钮文字 */
  children?: ReactNode;
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-white hover:bg-accent-2 active:bg-accent-2 disabled:bg-accent/50 shadow-sm',
  secondary:
    'bg-surface text-ink-2 border border-line hover:bg-surface-2 hover:text-ink disabled:opacity-50',
  ghost: 'bg-transparent text-ink-2 hover:bg-surface-2 hover:text-ink disabled:opacity-50',
  danger: 'bg-bad text-white hover:opacity-90 active:opacity-80 disabled:opacity-50 shadow-sm',
};

const SIZE_CLASS: Record<ButtonSize, string> = {
  sm: 'px-2.5 py-1 text-xs gap-1.5 min-h-[28px]',
  md: 'px-3 py-1.5 text-sm gap-2 min-h-[36px]',
  lg: 'px-4 py-2.5 text-sm gap-2 min-h-[44px] sm:text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled,
    iconLeft,
    iconRight,
    block = false,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={twMerge(
        'inline-flex items-center justify-center font-medium rounded-md',
        'transition-colors duration-150',
        'focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2',
        'disabled:cursor-not-allowed',
        block && 'w-full',
        VARIANT_CLASS[variant],
        SIZE_CLASS[size],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Spinner
          size={size === 'lg' ? 'sm' : 'xs'}
          variant="ring"
          className={size === 'sm' ? 'text-current' : 'text-current'}
        />
      ) : (
        iconLeft && <Icon name={iconLeft} size={size === 'lg' ? 'md' : 'sm'} aria-hidden />
      )}
      {children}
      {!loading && iconRight && (
        <Icon name={iconRight} size={size === 'lg' ? 'md' : 'sm'} aria-hidden />
      )}
    </button>
  );
});
